import React from "react";
import {
  Card, Typography, List, ListItem, ListItemAvatar, Avatar, ListItemText,
  Box, Button
} from "@mui/material";
import { CheckCircleRounded } from "@mui/icons-material";

import {
  brandThemeCssVars,
  DEFAULT_BRAND_THEME,
  normalizeBrandTheme,
} from "../themeTokens";

export default function OrderSummary({
  id,
  currency,
  cartProducts = {},
  cartCombos = {},
  gross = 0,
  onNewOrder,
  t = (key, fallback) => fallback || key,
  brandTheme = DEFAULT_BRAND_THEME,
}) {
  const c = normalizeBrandTheme(brandTheme);
  const products = Object.values(cartProducts);
  const combos = Object.entries(cartCombos).map(([k, v]) => ({ key: k, ...v }));
  const money = (n) => new Intl.NumberFormat("ro-RO", { style: "currency", currency, maximumFractionDigits: 2 }).format(n);
  const fromCents = (c) => Math.round(c) / 100;

  return (
    <Card
      component="section"
      role="region"
      aria-labelledby="order-summary-title"
      elevation={0}
      sx={{
        ...brandThemeCssVars(c),
        height: { xs: "calc(100dvh - 20px)", sm: "80vh" },
        maxHeight: { xs: "calc(100dvh - 20px)", sm: 720 },
        borderRadius: { xs: 3, sm: 4 },
        border: "1px solid",
        borderColor:
          "color-mix(in srgb, var(--brand-text, #431407) 12%, transparent)",
        overflow: "hidden",
        boxShadow: "0 24px 70px rgba(33,20,12,0.14)",
        bgcolor: "var(--brand-bg, #fffaf3)",
        display: "flex",
        flexDirection: "column",
        "@media (prefers-reduced-motion: reduce)": {
          "&, & *": {
            animationDuration: "0.01ms !important",
            transitionDuration: "0.01ms !important",
            scrollBehavior: "auto !important",
          },
        },
      }}
    >
      {/* Fixed top summary header */}
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: "var(--brand-surface, #fff)",
          borderBottom: "1px solid",
          borderColor:
            "color-mix(in srgb, var(--brand-text, #431407) 10%, transparent)",
          flexShrink: 0,
        }}
      >
        <Box sx={{ textAlign: "center" }} aria-live="polite">
          <Box
            role="img"
            aria-label={t("summary.confirmed")}
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "rgba(46, 125, 50, 0.10)",
              color: "success.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 1.25,
              boxShadow: "0 12px 30px rgba(46,125,50,0.14)",
            }}
          >
            <CheckCircleRounded sx={{ fontSize: 38 }} />
          </Box>

          <Typography
            id="order-summary-title"
            variant="h5"
            sx={{
              fontWeight: 900,
              letterSpacing: -0.3,
              mb: 0.35,
            }}
          >
            {t("summary.confirmed")}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {t("summary.order")} #{id}
          </Typography>
        </Box>
      </Box>

      {/* Scrollable product list */}
      <Box
        id="order-summary"
        component="section"
        role="region"
        aria-labelledby="order-summary-items-title"
        tabIndex={0}
        sx={{
          flex: 1,
          overflowY: "auto",
          p: { xs: 1.5, sm: 2 },
          overscrollBehavior: "contain",
          "&:focus-visible": {
            outline: "3px solid var(--brand-cta, #0f766e)",
            outlineOffset: -3,
          },
        }}
      >
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2.5,
            bgcolor: "var(--brand-surface, #fff)",
            border: "1px solid",
            borderColor:
              "color-mix(in srgb, var(--brand-text, #431407) 10%, transparent)",
            boxShadow: "0 10px 28px rgba(33,20,12,0.06)",
          }}
        >
          <Typography
            id="order-summary-items-title"
            variant="subtitle2"
            sx={{
              fontWeight: 900,
              mb: 1,
            }}
          >
            {t("summary.orderedProducts")}
          </Typography>

          <List dense sx={{ p: 0 }}>
            {products.map((it) => {
              const unit = fromCents(it.unitPriceC);
              const lineTotal = unit * it.qty;

              return (
                <ListItem
                  key={`p-${it.id}`}
                  disableGutters
                  sx={{
                    py: 0.75,
                    gap: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-child": {
                      borderBottom: 0,
                    },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Avatar
                      variant="rounded"
                      src={it.imageSmallUrl || undefined}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2.25,
                        bgcolor: "var(--brand-bg, #fffaf3)",
                        border: "1px solid",
                        borderColor:
                          "color-mix(in srgb, var(--brand-text, #431407) 10%, transparent)",
                      }}
                    >
                      {it.name?.[0] || "?"}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 800,
                          lineHeight: 1.25,
                        }}
                      >
                        {it.name}
                      </Typography>
                    }
                    secondary={
                      it.qty > 1 ? (
                        <Typography variant="caption" color="text.secondary">
                          {it.qty} × {money(unit)}
                        </Typography>
                      ) : null
                    }
                  />

                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 900,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {money(lineTotal)}
                  </Typography>
                </ListItem>
              );
            })}

            {combos.map((it) => {
              const unit = fromCents(it.basePriceC);
              const lineTotal = unit * it.qty;
              const selectionNames = Object.values(it.selections || {})
                .map((s) => s.name || s)
                .filter(Boolean);

              return (
                <ListItem
                  key={`c-${it.key}`}
                  disableGutters
                  sx={{
                    py: 0.75,
                    gap: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-child": {
                      borderBottom: 0,
                    },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Avatar
                      variant="rounded"
                      src={it.imageSmallUrl || undefined}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2.25,
                        bgcolor:
                          "color-mix(in srgb, var(--brand-cta, #0f766e) 8%, var(--brand-surface, #fff))",
                        border: "1px solid",
                        borderColor:
                          "color-mix(in srgb, var(--brand-cta, #0f766e) 18%, transparent)",
                      }}
                    >
                      🧩
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 800,
                          lineHeight: 1.25,
                        }}
                      >
                        {it.name} · Combo
                      </Typography>
                    }
                    secondary={
                      selectionNames.length ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {selectionNames.join(", ")}
                        </Typography>
                      ) : null
                    }
                  />

                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 900,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {money(lineTotal)}
                  </Typography>
                </ListItem>
              );
            })}
          </List>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: "center",
            mt: 2,
            lineHeight: 1.5,
          }}
        >
          {t("summary.keepConfirmation")}
        </Typography>
      </Box>

      {/* Fixed footer */}
      <Box
        sx={{
          p: 2,
          pb: "max(16px, env(safe-area-inset-bottom))",
          bgcolor: "var(--brand-surface, #fff)",
          borderTop: "1px solid",
          borderColor:
            "color-mix(in srgb, var(--brand-text, #431407) 10%, transparent)",
          boxShadow: "0 -10px 28px rgba(0,0,0,0.08)",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2.5,
            bgcolor:
              "color-mix(in srgb, var(--brand-primary, #f7b500) 9%, var(--brand-surface, #fff))",
            border: "1px solid",
            borderColor:
              "color-mix(in srgb, var(--brand-primary, #f7b500) 24%, transparent)",
            mb: 1.25,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
              {t("summary.totalPaid")}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                lineHeight: 1.15,
              }}
            >
              {money(gross)}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          fullWidth
          onClick={onNewOrder}
          sx={{
            minHeight: 50,
            borderRadius: 2.5,
            fontWeight: 900,
            py: 1.15,
            bgcolor: c.cta,
            color: c.onCta,
            boxShadow: "0 12px 28px rgba(15,118,110,0.22)",
            "&:hover": {
              bgcolor: c.cta,
              filter: "brightness(0.94)",
            },
            "&:focus-visible": {
              outline: `3px solid ${c.cta}`,
              outlineOffset: 3,
            },
          }}
        >
          {t("summary.newOrder")}
        </Button>
      </Box>
    </Card>
  );
}
